import { useMemo, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';

import { FHPagination, FHSkeletonLoader } from '../../base/';
import CardSkeletonLoader from './CardSkeletonLoader';
import TreatmentPlanItem from './TreatmentPlanItem';

import type { ITreatmentPlan } from '../../../utils/types';

interface InProgressTreatmentPlansProps {
  loading: boolean;
  treatmentPlans: ITreatmentPlan[];
}

const SlideChunkSize = 8;

function InProgressTreatmentPlans(props: InProgressTreatmentPlansProps) {
  const { loading, treatmentPlans } = props;
  const [currentPage, setCurrentPage] = useState(1);

  const chunkedArray = useMemo(() => {
    if (!treatmentPlans) return [];

    const group = [];
    for (let i = 0; i < treatmentPlans.length; i += SlideChunkSize) {
      group.push(treatmentPlans.slice(i, i + SlideChunkSize));
    }
    return group;
  }, [treatmentPlans]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {loading ? (
        <Grid container spacing={{ xs: 0.5, sm: 2, md: 2 }}>
          {[...Array(SlideChunkSize)].map((_, index) => (
            <Grid key={index} item xs={12} sm={6} md={4} lg={3} mb={1}>
              <CardSkeletonLoader tpLoader />
            </Grid>
          ))}
        </Grid>
      ) : treatmentPlans.length ? (
        <Grid container spacing={{ xs: 2, sm: 2, md: 2 }}>
          {chunkedArray[currentPage - 1].map((tp: ITreatmentPlan) => (
            <Grid key={tp.id} item xs={12} sm={6} md={4} lg={3}>
              <TreatmentPlanItem treatmentPlan={tp} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Typography ml={2}>There is no treatment plan in progress.</Typography>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
          <FHSkeletonLoader containerStyles={{ width: '22%' }} height={30} />
        </Box>
      ) : (
        chunkedArray.length > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
            <FHPagination
              count={chunkedArray.length}
              currentPage={currentPage}
              handlePageChange={handlePageChange}
            />
          </Box>
        )
      )}
    </Box>
  );
}

export default InProgressTreatmentPlans;
